/**
 * The deals board: one column per stage, each with its count, total and
 * weighted total, and a card per deal.
 *
 * Dragging a card to another column moves the deal there. It uses the
 * browser's own drag and drop rather than a library: a column takes a drop
 * and the card says which deal it is. A drag cannot be done on a phone or
 * from a keyboard, so every card also has a Move to menu that does the same.
 *
 * The board scrolls sideways inside its own box, so the page never does.
 */
import { useState, type DragEvent } from 'react';
import { ActionIcon, Badge, Menu, Text, Tooltip } from '@mantine/core';
import { IconArrowsRightLeft } from '@tabler/icons-react';
import type { DealSummary, Pipeline, PipelineStage, StageTotals } from '../../api/crm';
import { FONTS, TOKENS } from '../../theme';
import { MonoLabel } from '../../ui';
import { useVocab } from '../../vocab';
import { formatDay, formatPence, statusColour, STATUS_LABEL } from './crmShared';

const DRAG_TYPE = 'application/x-rowstorm-deal';

/** The stages a board shows: every active one, and an archived one only while deals are in it. */
export function boardStages(pipeline: Pipeline, deals: DealSummary[]): PipelineStage[] {
  return pipeline.stages
    .filter((s) => !s.archived || deals.some((d) => d.stage_id === s.id))
    .sort((a, b) => Number(a.archived) - Number(b.archived) || a.position - b.position);
}

export function DealBoard({
  pipeline, deals, totals, canEdit, movingId, onOpen, onMove,
}: {
  pipeline: Pipeline;
  deals: DealSummary[];
  totals: StageTotals[] | undefined;
  canEdit: boolean;
  movingId: string | null;
  onOpen: (id: string) => void;
  onMove: (deal: DealSummary, stage: PipelineStage) => void;
}) {
  const v = useVocab();
  const stages = boardStages(pipeline, deals);
  const [over, setOver] = useState<string | null>(null);

  function drop(e: DragEvent, stage: PipelineStage) {
    e.preventDefault();
    setOver(null);
    const id = e.dataTransfer.getData(DRAG_TYPE) || e.dataTransfer.getData('text/plain');
    const deal = deals.find((d) => d.id === id);
    if (deal && deal.stage_id !== stage.id) onMove(deal, stage);
  }

  return (
    <div
      data-testid="deal-board"
      style={{ overflowX: 'auto', width: '100%', paddingBottom: 8, WebkitOverflowScrolling: 'touch' }}
    >
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', width: 'max-content' }}>
        {stages.map((stage) => {
          const cards = deals.filter((d) => d.stage_id === stage.id);
          const t = totals?.find((x) => x.stage_id === stage.id);
          const highlighted = over === stage.id;
          return (
            <section
              key={stage.id}
              data-testid={`stage-${stage.id}`}
              aria-label={stage.name}
              onDragOver={(e) => {
                if (!canEdit) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                if (over !== stage.id) setOver(stage.id);
              }}
              onDragLeave={() => setOver((o) => (o === stage.id ? null : o))}
              onDrop={(e) => canEdit && drop(e, stage)}
              style={{
                width: 272,
                flex: 'none',
                background: highlighted ? TOKENS.hoverTint : TOKENS.surface2,
                border: `1px solid ${highlighted ? TOKENS.borderStrong : TOKENS.border}`,
                borderRadius: 10,
                padding: 10,
                minHeight: 160,
                transition: 'background 120ms ease, border-color 120ms ease',
              }}
            >
              <header style={{ marginBottom: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'baseline' }}>
                  <Text fw={600} size="sm" style={{ color: TOKENS.text }} truncate>
                    {stage.name}
                  </Text>
                  <MonoLabel>{`${t?.count ?? cards.length}`}</MonoLabel>
                </div>
                <div
                  style={{
                    display: 'flex', justifyContent: 'space-between', gap: 8, marginTop: 4,
                    fontFamily: FONTS.mono, fontSize: 12, fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  <span style={{ color: TOKENS.text }} data-testid={`stage-total-${stage.id}`}>
                    {formatPence(t?.amount_pence ?? 0)}
                  </span>
                  <Tooltip label={`Weighted at ${stage.win_probability}%`} withArrow>
                    <span style={{ color: TOKENS.textSecondary }} data-testid={`stage-weighted-${stage.id}`}>
                      {`${formatPence(t?.weighted_pence ?? 0)} weighted`}
                    </span>
                  </Tooltip>
                </div>
                {stage.archived && <Text size="xs" c="dimmed" mt={2}>Archived stage</Text>}
              </header>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {cards.map((d) => (
                  <DealCard
                    key={d.id}
                    deal={d}
                    stages={stages}
                    canEdit={canEdit}
                    moving={movingId === d.id}
                    onOpen={() => onOpen(d.id)}
                    onMove={(s) => onMove(d, s)}
                  />
                ))}
                {cards.length === 0 && (
                  <Text size="xs" c="dimmed" ta="center" py="md">
                    {canEdit ? `Drag ${v.a('deal')} here` : `No ${v.many('deal')}`}
                  </Text>
                )}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}

function DealCard({
  deal, stages, canEdit, moving, onOpen, onMove,
}: {
  deal: DealSummary;
  stages: PipelineStage[];
  canEdit: boolean;
  moving: boolean;
  onOpen: () => void;
  onMove: (s: PipelineStage) => void;
}) {
  return (
    <article
      data-testid={`deal-${deal.id}`}
      draggable={canEdit}
      onDragStart={(e) => {
        e.dataTransfer.setData(DRAG_TYPE, deal.id);
        e.dataTransfer.setData('text/plain', deal.id);
        e.dataTransfer.effectAllowed = 'move';
      }}
      style={{
        background: TOKENS.surface,
        border: `1px solid ${TOKENS.border}`,
        borderRadius: 8,
        boxShadow: TOKENS.shadowCard,
        padding: '8px 10px',
        cursor: canEdit ? 'grab' : 'pointer',
        opacity: moving ? 0.55 : 1,
      }}
    >
      <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
        <button
          type="button"
          onClick={onOpen}
          style={{
            all: 'unset', cursor: 'pointer', flex: 1, minWidth: 0, color: TOKENS.text,
            fontSize: 14, fontWeight: 500, lineHeight: 1.3, overflowWrap: 'anywhere',
          }}
        >
          {deal.name}
        </button>
        {canEdit && (
          <Menu position="bottom-end" withinPortal shadow="md">
            <Menu.Target>
              <ActionIcon variant="subtle" size="sm" aria-label={`Move ${deal.name}`}>
                <IconArrowsRightLeft size={14} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Label>Move to</Menu.Label>
              {stages.filter((s) => !s.archived).map((s) => (
                <Menu.Item key={s.id} disabled={s.id === deal.stage_id} onClick={() => onMove(s)}>
                  {s.name}
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>
        )}
      </div>
      {deal.company_name && (
        <Text size="xs" c="dimmed" mt={2} truncate>{deal.company_name}</Text>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginTop: 6 }}>
        <span style={{ fontFamily: FONTS.mono, fontSize: 12, color: TOKENS.text, fontVariantNumeric: 'tabular-nums' }}>
          {formatPence(deal.amount_pence)}
        </span>
        {deal.status !== 'open' ? (
          <Badge size="xs" variant="light" color={statusColour(deal.status)}>{STATUS_LABEL[deal.status]}</Badge>
        ) : (
          <Text size="xs" c="dimmed">{deal.close_date ? formatDay(deal.close_date) : ''}</Text>
        )}
      </div>
      {deal.owner_name && <Text size="xs" c="dimmed" mt={2} truncate>{deal.owner_name}</Text>}
    </article>
  );
}
